/** @jsx isolateComponent */

/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useState } from 'react';
import type { ReactNode, MouseEvent as ReactMouseEvent } from 'react';

import { css, keyframes } from '@emotion/core';
import { Zindexes } from '@zapier/theme';

import MenuCollapseButton from '@zapier/universal-nav/dist/components/MenuCollapseButton';
/* eslint-disable import/no-duplicates */
import UniversalTopbar from '@zapier/universal-nav/dist/components/UniversalTopbar';
import type { UniversalTopbarProps } from '@zapier/universal-nav/dist/components/UniversalTopbar';
import UniversalSidebar, {
  UniversalSidebarContainer,
} from '@zapier/universal-nav/dist/components/UniversalSidebar';
import { Animation, Button } from '@zapier/design-system';
import { useUniversalNavContext } from '@zapier/universal-nav/dist/hooks/useUniversalNavContext';
import { addParamsToUrl, extractParams } from '@zapier/url-utils';
import { isolateComponent, labelStyles } from '@zapier/common-utils';

import findAccountsMenuRenderer from '@zapier/universal-nav/dist/utils/findAccountsMenuRenderer';
import findNotificationsRenderer from '@zapier/universal-nav/dist/utils/findNotificationsRenderer';
import useUnscrollableBody from '@zapier/universal-nav/dist/hooks/useUnscrollableBody';
import {
  UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
  UNIVERSAL_TOPBAR_HEIGHT,
  UniversalTopbarLinks,
  loginPath,
  signupPath,
} from '@zapier/universal-nav/dist/constants';

import type { UniversalNavLinkType } from '@zapier/universal-nav/dist/constants';

const slideAnimation = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(0); }
`;

const Styles = labelStyles(__filename, {

  root: ({ topbarSize }: { topbarSize: 'small' | 'medium' }) => [
    css`
      position: sticky;
      z-index: ${Zindexes.universalNav};
    `,
    {
      small: css`top: ${-UNIVERSAL_TOPBAR_CONDENSED_HEIGHT}px;`,
      medium: css`top: ${-UNIVERSAL_TOPBAR_HEIGHT}px;`,
    }[topbarSize],
  ],

  sidebarCtaButtons: css`
    display: grid;
    gridTemplateColumns: 1fr 1fr;
    gap: 10px;
  `,

  sidebarWrapper: ({
    isFixed,
    isFullWidth,
    topbarSize
  }: {
    isFixed: boolean;
    isFullWidth: boolean;
    topbarSize: 'small' | 'medium';
  }) => [
    css`
      position: absolute;
      display: flex;
      align-items: stretch;
      // Push beneath UniversalTopbar border so the border is seen.
      margin-top: 1px;
      left: 0;
      height: 100vh;
      will-change: height;
      transition: height 300ms linear;
    `,

    {
      small: css`top: ${UNIVERSAL_TOPBAR_CONDENSED_HEIGHT}px;`,
      medium: css`top: ${UNIVERSAL_TOPBAR_HEIGHT}px;`,
    }[topbarSize],

    isFixed && !isFullWidth && css`
      position: fixed;
      top: 0;
      bottom: 0;
      height: auto;
    `,

    isFullWidth && css`
      // Pull above UniversalTopbar border so the border is hidden.
      margin-top: 0;
      width: 100%;
    `,

    !isFullWidth && css`
      animation: ${slideAnimation} ${Animation.transitionDuration} ease-in-out;
    `,
  ]

});

export type Props = {
  /**
   * Renders notifications component.
   */
  renderNotifications: () => ReactNode;
  /**
   * Anchor link to the main content area on the page to allow
   * screenreaders to skip to the main content.
   */
  skipLinkHref?: string;
};

type SidebarProps = {
  currentAccountId?: number;
  currentUrl: string;
  height: string | number;
  isFixed: boolean;
  isLoggedIn: boolean;
  isOnAppPage: boolean;
  isSidebarControlledByTopbar: boolean;
  isSidebarForcedOpen: boolean;
  links: Array<UniversalNavLinkType>;
  loginHref: string;
  onClick?: (e: ReactMouseEvent) => void;
  shouldRenderTopbarLinks: boolean;
  signupHref: string;
  size: 'small' | 'medium';
  skipLinkHref?: string;
  topbarSize: 'small' | 'medium';
};

// Copies query strings from given source to destination
const copyQueryString = (source: string, destination: string) =>
  addParamsToUrl(destination, extractParams(source), true);

const Sidebar = (props: SidebarProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const isFullWidth = props.size === 'small';
  const isActuallyCollapsed =
    !isFullWidth && !props.isSidebarForcedOpen && !props.isSidebarControlledByTopbar && isCollapsed;
  const sharedProps = {
    isCollapsed: isActuallyCollapsed,
    isFullWidth,
    isShadowed:
      !isFullWidth && !props.isSidebarForcedOpen && !isActuallyCollapsed,
    onClick: props.onClick,
    onMouseEnter: () => {
      if (window.matchMedia('(hover: hover)').matches) {
        setIsCollapsed(false);
      }
    },
    onMouseLeave: () => {
      if (window.matchMedia('(hover: hover)').matches) {
        setIsCollapsed(true);
      }
    },
    selectedHref: props.currentUrl,
    skipLinkHref: props.skipLinkHref,
  };

  const shouldRenderLoggedInSidebar =
    props.isLoggedIn &&
    props.currentAccountId &&
    (props.isOnAppPage || props.shouldRenderTopbarLinks);

  useUnscrollableBody({ shouldAllowScroll: !isFullWidth && !sharedProps.isShadowed });

  const sidebar = shouldRenderLoggedInSidebar ? (
    <UniversalSidebarContainer
      {...sharedProps}
      currentAccountId={props.currentAccountId!}
    />
  ) : (
    <UniversalSidebar
      {...sharedProps}
      links={props.links}
      logoAriaLabel="Go to Zapier homepage"
      logoHref="/"
      renderFooter={() => {
        if (props.size === 'small' && !isActuallyCollapsed) {
          if (!props.isLoggedIn) {
            return (
              <div css={Styles.sidebarCtaButtons}>
                <Button
                  color="greyAlt"
                  href={props.loginHref}
                  size="small"
                >
                  Log in
                </Button>
                <Button
                  color="blue"
                  href={props.signupHref}
                  size="small"
                >
                  Sign up
                </Button>
              </div>
            );
          }
          if (props.isLoggedIn && !props.isOnAppPage) {
            return (
              <Button
                color="black"
                href="/app/zaps"
                isFullWidth={true}
                size="medium"
              >
                My Zaps
              </Button>
            );
          }
        }
        return null;
      }}
    />
  );

  const style = { height: props.height };
  return (
    <div css={Styles.sidebarWrapper({
      isFixed: props.isFixed,
      isFullWidth,
      topbarSize: props.topbarSize,
    })} style={style}>
      {sidebar}
    </div>
  );
};

/**
 * Top-level universal navigation component that renders the
 * top bar and the sidebar.
 */
export const UniversalNav = (props: Props) => {
  const {
    accountSwitcherUrl,
    canTopbarFitBackToAppButton,
    canForceSidebarOpen,
    currentAccountId,
    currentUrl,
    isLoadingLoggedIn,
    isLoggedIn,
    isOnAppPage,
    isSearchFullWidth,
    isSidebarForcedOpen,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    onToggleSidebarVisibility,
    setIsSidebarForcedOpen,
    setIsSidebarOpenedByTopbar,
    setIsSidebarRendered,
    shouldRenderSearch,
    shouldRenderSidebar,
    shouldRenderTopbarLinks,
    topbarSize,
  } = useUniversalNavContext();

  useEffect(() => {
    setIsSidebarRendered(shouldRenderSidebar);
    return () => {
      setIsSidebarRendered(false);
    };
  }, [setIsSidebarRendered, shouldRenderSidebar]);

  const linksToRender = UniversalTopbarLinks;
  const loginHref = copyQueryString(currentUrl, loginPath);
  const signupHref = copyQueryString(currentUrl, signupPath);

  const renderAccountMenu = findAccountsMenuRenderer({
    accountSwitcherUrl,
    canTopbarFitBackToAppButton,
    currentAccountId,
    currentUrl,
    isLoadingLoggedIn,
    isLoggedIn,
    loginHref,
    signupHref,
  });

  const renderNotifications = findNotificationsRenderer({
    currentUrl,
    isLoggedIn,
    renderNotifications: props.renderNotifications,
  });

  // Use `undefined` to leverage default value, noop to prevent rendering.
  const renderSearchForm = shouldRenderSearch ? undefined : () => undefined;

  const [bottom, setBottom] = useState(Infinity);
  const navRef = useRef(null);
  useEffect(() => {
    const onScroll = () => {
      if (navRef.current) {
        setBottom((navRef.current! as HTMLDivElement)!.getBoundingClientRect().bottom);
      }
    };
    onScroll();
    document.addEventListener('scroll', onScroll);
    return () => {
      document.removeEventListener('scroll', onScroll);
    };
  }, [setBottom]);

  const closeSidebar = () => {
    setIsSidebarOpenedByTopbar(false);
  };

  const closeSidebarOnTriggerClick = (e: ReactMouseEvent | MouseEvent | TouchEvent) => {
    const target = e!.target! as HTMLElement;
    const trigger = target.closest('a, button, label, [role="button"]');
    // Close the sidebar if any interactive node is clicked
    // and the sidebar is controlled by the topbar.
    if (isSidebarOpenedByTopbar && !!trigger) {
      closeSidebar();
    }
  };

  const renderSidebar = (topbarProps: UniversalTopbarProps) => {
    const toggleSidebarVisibilityButton = (() => {
      if (onToggleSidebarVisibility) {
        return (
          <MenuCollapseButton
            ariaExpanded={isSidebarOpenedByTopbar}
            icon={isSidebarOpenedByTopbar ? 'formX' : 'navMenu'}
            label="Menu"
            onClick={onToggleSidebarVisibility}
          />
        );
      }
      if (canForceSidebarOpen) {
        return (
          <MenuCollapseButton
            ariaExpanded={isSidebarForcedOpen}
            label="Keep sidebar open"
            onClick={() => setIsSidebarForcedOpen(!isSidebarForcedOpen)}
          />
        );
      }
      return null;
    })();
    const sidebar = shouldRenderSidebar && (
      <Sidebar
        currentAccountId={currentAccountId}
        currentUrl={currentUrl}
        height={`calc(100vh - ${Math.max(0, bottom)}px)`}
        isFixed={bottom <= 0}
        isLoggedIn={isLoggedIn}
        isOnAppPage={isOnAppPage}
        isSidebarControlledByTopbar={isSidebarControlledByTopbar}
        isSidebarForcedOpen={isSidebarForcedOpen}
        links={linksToRender}
        loginHref={loginHref}
        onClick={closeSidebarOnTriggerClick}
        shouldRenderTopbarLinks={shouldRenderTopbarLinks}
        signupHref={signupHref}
        size={topbarProps.size}
        skipLinkHref={props.skipLinkHref}
        topbarSize={topbarSize}
      />
    );
    // Need to return the nodes only if at least one exists,
    // otherwise the empty `Fragment` will cause a slot to
    // render when it shouldn't.
    return (
      (toggleSidebarVisibilityButton || sidebar) && (
        <React.Fragment>
          {toggleSidebarVisibilityButton}
          {sidebar}
        </React.Fragment>
      )
    );
  };

  return (
    <div css={Styles.root({ topbarSize })} ref={navRef}>
      <UniversalTopbar
        isSearchFullWidth={isSearchFullWidth}
        links={shouldRenderTopbarLinks ? linksToRender : []}
        onClick={closeSidebarOnTriggerClick}
        onClickOutside={closeSidebar}
        renderAccountMenu={renderAccountMenu}
        renderNotifications={renderNotifications}
        renderSearchForm={renderSearchForm}
        renderSidebar={renderSidebar}
        selectedHref={currentUrl}
        size={topbarSize}
        skipLinkHref={props.skipLinkHref}
      />
    </div>
  );
};

UniversalNav.defaultProps = {
  renderNotifications: () => null,
};
